// =============================================================================
// MeuDriver — usePayouts (Batch J2)
// =============================================================================
// Hooks para gerenciamento de payouts (saques) de instrutores.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface InstructorPayout {
  id: string
  instructor_id: string
  amount: number
  mp_transfer_id: string | null
  mp_recipient_id: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message: string | null
  requested_at: string
  processed_at: string | null
}

export interface InstructorMpAccount {
  id: string
  instructor_id: string
  mp_user_id: string
  mp_email: string | null
  verified: boolean
  created_at: string
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const payoutKeys = {
  all: ['payouts'] as const,
  instructor: (userId: string) => [...payoutKeys.all, 'instructor', userId] as const,
  admin: () => [...payoutKeys.all, 'admin'] as const,
  mpAccount: (userId: string) => ['mp_accounts', userId] as const,
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

async function fetchInstructorPayouts(userId: string): Promise<InstructorPayout[]> {
  const { data, error } = await supabase
    .from('instructor_payouts')
    .select('*')
    .eq('instructor_id', userId)
    .order('requested_at', { ascending: false })

  if (error) throw new Error(error.message)
  return (data ?? []) as InstructorPayout[]
}

async function fetchAllPayouts(): Promise<InstructorPayout[]> {
  const { data, error } = await supabase
    .from('instructor_payouts')
    .select('*, instructor:profiles!instructor_payouts_instructor_id_fkey(full_name, avatar_url)')
    .order('requested_at', { ascending: false })

  if (error) throw new Error(error.message)
  return (data ?? []) as unknown as InstructorPayout[]
}

async function fetchMpAccount(userId: string): Promise<InstructorMpAccount | null> {
  const { data, error } = await supabase
    .from('instructor_mp_accounts')
    .select('*')
    .eq('instructor_id', userId)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data as InstructorMpAccount | null
}

// ---------------------------------------------------------------------------
// Hooks públicos
// ---------------------------------------------------------------------------

/**
 * Lista os payouts do instrutor autenticado.
 */
export function useInstructorPayouts() {
  const { user } = useAuth()

  return useQuery<InstructorPayout[], Error>({
    queryKey: payoutKeys.instructor(user?.id ?? ''),
    queryFn:  () => fetchInstructorPayouts(user!.id),
    enabled:  Boolean(user?.id),
    staleTime: 1000 * 60,
  })
}

/**
 * Lista todos os payouts — admin only.
 */
export function useAdminPayouts() {
  return useQuery<InstructorPayout[], Error>({
    queryKey: payoutKeys.admin(),
    queryFn:  fetchAllPayouts,
    staleTime: 1000 * 60,
  })
}

/**
 * Lê a conta Mercado Pago do instrutor autenticado.
 */
export function useInstructorMpAccount() {
  const { user } = useAuth()

  return useQuery<InstructorMpAccount | null, Error>({
    queryKey: payoutKeys.mpAccount(user?.id ?? ''),
    queryFn:  () => fetchMpAccount(user!.id),
    enabled:  Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Salva/atualiza a conta MP do instrutor.
 */
export function useSaveInstructorMpAccount() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { mp_user_id: string; mp_email?: string }>({
    mutationFn: async (payload) => {
      if (!user?.id) throw new Error('Não autenticado')

      const { error } = await supabase
        .from('instructor_mp_accounts')
        .upsert({
          instructor_id: user.id,
          mp_user_id: payload.mp_user_id,
          mp_email: payload.mp_email ?? null,
        }, { onConflict: 'instructor_id' })

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: payoutKeys.mpAccount(user.id) })
      }
    },
  })
}

/**
 * Solicita novo saque: cria registro via RPC e chama a edge function process-payout.
 */
export function useRequestPayout() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { amount: number; mp_recipient_id: string }>({
    mutationFn: async (payload) => {
      if (!user?.id) throw new Error('Não autenticado')

      // 1. Cria registro de payout via RPC (SECURITY DEFINER faz o INSERT)
      const { data: payoutId, error: rpcError } = await supabase.rpc(
        'request_instructor_payout',
        { p_amount: payload.amount, p_mp_recipient: payload.mp_recipient_id },
      )

      if (rpcError) throw new Error(rpcError.message)
      if (!payoutId) throw new Error('Não foi possível criar o registro de saque')

      // 2. Chama edge function para processar o saque no MP
      const { error: fnError } = await supabase.functions.invoke('process-payout', {
        body: { payout_id: payoutId },
      })

      // Falha da edge function não impede o registro — o payout fica em
      // 'processing'/'failed' e pode ser retentado
      if (fnError) {
        console.warn('[useRequestPayout] process-payout error:', fnError.message)
        throw new Error('Saque registrado, mas houve um problema ao processar. Tente novamente.')
      }
    },
    onSettled: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: payoutKeys.instructor(user.id) })
      }
    },
  })
}
